import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../../lib/api';
import { Button, Input, Card } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';

const MAIN_AGENCY_ID = 'a57012f5-7914-4fbc-85c1-90c9a1480397';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  agentId: '',
  mdOrTrainer: '',
};

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildAccessRequestHtml(formData) {
  const row = (label, value) => {
    const display = value?.trim() || '—';
    return `
      <tr>
        <td style="padding:10px 12px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;white-space:nowrap;">${label}</td>
        <td style="padding:10px 12px;color:#111827;border-bottom:1px solid #e5e7eb;">${escapeHtml(display)}</td>
      </tr>
    `;
  };

  return `
    <div style="font-family:Arial,Helvetica,sans-serif;max-width:560px;margin:0 auto;color:#111827;">
      <h2 style="margin:0 0 16px;font-size:20px;color:#00AAFF;">New CRM Access Request</h2>
      <p style="margin:0 0 20px;color:#4b5563;font-size:14px;">Someone submitted a request for CRM access.</p>
      <table style="width:100%;border-collapse:collapse;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;">
        ${row('First Name', formData.firstName)}
        ${row('Last Name', formData.lastName)}
        ${row('Phone Number', formData.phone)}
        ${row('Email', formData.email)}
        ${row('Agent ID', formData.agentId)}
        ${row('Marketing Director or Trainer Name', formData.mdOrTrainer)}
      </table>
    </div>
  `;
}

async function sendAccessRequestEmail({ to_email, subject, html_body, reply_to }) {
  const { data, error } = await supabase.functions.invoke('brevo-email', {
    body: { to_email, subject, html_body, reply_to, access_request: true },
  });

  if (error) {
    let msg = error.message || 'Could not send email';
    if (error instanceof FunctionsHttpError && error.context?.json) {
      try {
        const errBody = await error.context.json();
        if (errBody?.error && typeof errBody.error === 'string') msg = errBody.error;
      } catch {
        /* keep msg */
      }
    }
    throw new Error(msg);
  }

  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Could not send email');
  }
  if (!data?.success) {
    throw new Error('Could not send email');
  }
}

export default function Signup() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!supabase) {
        throw new Error('Request access requires Supabase configuration.');
      }

      const { data: admins, error: adminsError } = await supabase
        .from('users')
        .select('email')
        .eq('agency_id', MAIN_AGENCY_ID)
        .in('role', ['admin', 'super_admin'])
        .eq('is_active', true);

      if (adminsError) throw new Error(adminsError.message || 'Could not load admin recipients');
      if (!admins?.length) throw new Error('No administrators are available to receive access requests.');

      const subject = 'New CRM Access Request';
      const html_body = buildAccessRequestHtml(formData);
      const reply_to = formData.email.trim();

      await Promise.all(
        admins.map((admin) =>
          sendAccessRequestEmail({
            to_email: admin.email,
            subject,
            html_body,
            reply_to,
          })
        )
      );

      setSubmitted(true);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Request failed',
        message: err.message || 'Could not send your access request. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Request Access</h2>
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
              Sign in
            </Link>
          </p>
        </div>

        {submitted ? (
          <div className="text-center py-6">
            <p className="text-base text-gray-700">
              Your request has been sent! We&apos;ll reach out to you soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="John"
            />

            <Input
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Doe"
            />

            <Input
              label="Phone Number"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 555-5555"
            />

            <Input
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
            />

            <Input
              label="Agent ID (if you have one)"
              value={formData.agentId}
              onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
              placeholder="Optional"
            />

            <Input
              label="Marketing Director or Trainer Name"
              value={formData.mdOrTrainer}
              onChange={(e) => setFormData({ ...formData, mdOrTrainer: e.target.value })}
              placeholder="Optional"
            />

            <Button type="submit" className="w-full mt-6" isLoading={loading} size="lg">
              Send Request
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
